import React from 'react';
import styled from 'styled-components';
import SpeakerSelector, { Speaker } from '../content-components/SpeakerSelector';
import CheckIconAlt from '@src/svgs/CheckIconAlt';

interface SpeakerFilterComponentProps {
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  setSelectedSpeakers: (speakers: Speaker[]) => void;
  useSpeakerFilter: boolean;
  setUseSpeakerFilter: (useFilter: boolean | ((prev: boolean) => boolean)) => void;
  isLoadingSpeakers: boolean;
  title?: string;
  infoText?: string;
}

/**
 * Reusable component for speaker filtering
 * Can be used in any UI that needs to filter content by speaker
 */
const SpeakerFilterComponent: React.FC<SpeakerFilterComponentProps> = ({
  speakers,
  selectedSpeakers,
  setSelectedSpeakers,
  useSpeakerFilter,
  setUseSpeakerFilter,
  isLoadingSpeakers,
  title = "Filter by speaker",
  infoText = "Filter to only include content from selected speakers"
}) => {
  return (
    <SpeakerFilterSection>
      <CheckBoxContainer 
        onClick={() => setUseSpeakerFilter(prev => !prev)}
        disabled={isLoadingSpeakers || speakers.length === 0}
      >
        <CheckBox isChecked={useSpeakerFilter} role="button">
          {useSpeakerFilter && <CheckIconAlt />}
        </CheckBox>
        <div className="checkbox-text">{title}</div>
      </CheckBoxContainer>
      
      {useSpeakerFilter && (
        <>
          {isLoadingSpeakers ? (
            <LoadingText>Loading speakers...</LoadingText>
          ) : speakers.length === 0 ? (
            <NoSpeakersText>No speakers found in this video</NoSpeakersText>
          ) : (
            <SpeakerSelectorContainer>
              <SpeakerSelector
                speakers={speakers}
                selectedSpeakers={selectedSpeakers}
                onChange={setSelectedSpeakers}
              />
              <SpeakerInfo>
                {infoText}
              </SpeakerInfo>
            </SpeakerSelectorContainer>
          )}
        </>
      )}
    </SpeakerFilterSection>
  );
};

export default SpeakerFilterComponent;

// Styled components
const SpeakerFilterSection = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const CheckBoxContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  .checkbox-text {
    color: #f3e9d7;
    font-size: 12px;
    line-height: 140%;
    text-align: center;
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  border: 0;
  outline: 0;
  background-color: ${(props) => (props.isChecked ? '#4AD067' : 'transparent')};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: ${(props) => !props.isChecked && '1px solid #9B9B9B'};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingText = styled.div`
  color: #9B9B9B;
  font-size: 14px;
  text-align: center;
  padding: 10px 0;
  margin-top: 8px;
`;

const NoSpeakersText = styled.div`
  color: #9B9B9B;
  font-size: 12px;
  text-align: center;
  font-style: italic;
  padding: 8px 0;
  margin-top: 8px;
`;

const SpeakerSelectorContainer = styled.div`
  margin-top: 8px;
`;

const SpeakerInfo = styled.div`
  color: #9B9B9B;
  font-size: 12px;
  text-align: center;
  font-style: italic;
  padding: 8px 0;
  margin-top: 4px;
`;